import {
  Bell,
  Clapperboard,
  Inbox,
  LayoutGrid,
  Link as LinkIcon,
  type LucideIcon,
  Megaphone,
  MessagesSquare,
  Search,
  Sparkles,
  Store,
  TrendingUp,
  Users,
} from 'lucide-react';

import { cn } from '@components/lib/utils';
import {
  MARKET_VIEWS,
  VIEW_PARAM,
} from '@modules/Properties/home/market/marketModel';
import { CREATORS_TABS } from '@modules/Workspace/creators/CreatorsNav';

/* ─────────────────────────────────────────────────────────────────────────
 *  The one place the app's navigation is described. The desktop rail, its
 *  collapsed flyouts and the mobile bar/sheet all render from here, and
 *  `isNavItemActive` is the one answer to "is this where I am?".
 * ───────────────────────────────────────────────────────────────────────── */

export interface NavLocation {
  pathname: string;
  search: string;
}

export interface NavMatch {
  /** Path the item owns. Children count unless `exact`. */
  path: string;
  exact?: boolean;
  /** Query params that must match: a value, or a predicate over the raw value
   * (`null` when absent). Params not named here (evidence drawers, sort, …)
   * are ignored. */
  query?: Record<string, string | ((value: string | null) => boolean)>;
}

export interface NavItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  /** Where the link goes (path + optional query). */
  to: string;
  /** Rules that make this item active. Defaults to `{ path: to }`. */
  match?: NavMatch[];
}

export interface NavSection extends NavItem {
  children?: NavItem[];
}

const matches = (
  rule: NavMatch,
  { pathname, search }: NavLocation,
): boolean => {
  const pathOk = rule.exact
    ? pathname === rule.path
    : pathname === rule.path || pathname.startsWith(`${rule.path}/`);
  if (!pathOk) return false;
  if (!rule.query) return true;
  const params = new URLSearchParams(search);
  return Object.entries(rule.query).every(([key, want]) =>
    typeof want === 'function'
      ? want(params.get(key))
      : params.get(key) === want,
  );
};

const defaultMatch = (to: string): NavMatch[] => {
  const [path] = to.split('?');
  return [{ path }];
};

export const isNavItemActive = (
  item: Pick<NavItem, 'to' | 'match'>,
  location: NavLocation,
): boolean =>
  (item.match ?? defaultMatch(item.to)).some((rule) => matches(rule, location));

/* ── Market ─────────────────────────────────────────────────────────────── */

const MARKET_PATH = '/property/market';
const KNOWN_MARKET_VIEWS = MARKET_VIEWS.map((v) => v.key);

// `parseView` falls back to Pulse for a missing or unknown view; so does this.
const isPulseView = (v: string | null): boolean =>
  !KNOWN_MARKET_VIEWS.some((k) => k === v && k !== 'pulse');

const marketChildren: NavItem[] = MARKET_VIEWS.map(({ key, label }) => ({
  id: `market-${key}`,
  label,
  to: key === 'pulse' ? MARKET_PATH : `${MARKET_PATH}?${VIEW_PARAM}=${key}`,
  match: [
    {
      path: MARKET_PATH,
      exact: true,
      query: { [VIEW_PARAM]: key === 'pulse' ? isPulseView : key },
    },
    // A competitor's own page lives under Competitors.
    ...(key === 'competitors' ? [{ path: `${MARKET_PATH}/competitors` }] : []),
  ],
}));

/* ── Creators ───────────────────────────────────────────────────────────── */

const creatorsChildren: NavItem[] = CREATORS_TABS.map((tab) => ({
  id: `creators-${tab.to.slice(1)}`,
  label: tab.label,
  to: tab.to,
}));

/* ── Sections ───────────────────────────────────────────────────────────── */

export const HOME: NavSection = {
  id: 'home',
  label: 'Home',
  icon: LayoutGrid,
  to: '/property',
  match: [
    { path: '/property', exact: true },
    { path: '/property/series' },
    { path: '/dashboard' },
  ],
};

export const MARKET: NavSection = {
  id: 'market',
  label: 'Market',
  icon: TrendingUp,
  to: MARKET_PATH,
  children: marketChildren,
};

export const CREATORS: NavSection = {
  id: 'creators',
  label: 'Creators',
  icon: Users,
  to: '/creators',
  match: [
    { path: '/creators' },
    { path: '/search' },
    { path: '/saved-creators' },
  ],
  children: creatorsChildren,
};

export const CAMPAIGNS: NavSection = {
  id: 'campaigns',
  label: 'Campaigns',
  icon: Megaphone,
  to: '/campaigns',
};

export const OFFERS: NavSection = {
  id: 'offers',
  label: 'Offers',
  icon: Inbox,
  to: '/offers',
};

/** Org messaging (brand and creator orgs, behind INBOX_ENABLED). */
export const INBOX: NavSection = {
  id: 'inbox',
  label: 'Inbox',
  icon: MessagesSquare,
  to: '/inbox',
};

export const AI_CHAT: NavSection = {
  id: 'ai-chat',
  label: 'AI Chat',
  icon: Sparkles,
  to: '/ai-chat',
};

export const PROFILE: NavSection = {
  id: 'profile',
  label: 'Property profile',
  icon: Store,
  to: '/property/profile',
  match: [{ path: '/property/profile' }, { path: '/link-in-bio' }],
};

export const NOTIFICATIONS: NavSection = {
  id: 'notifications',
  label: 'Notifications',
  icon: Bell,
  to: '/notifications',
};

const CREATOR_HOME: NavSection = {
  id: 'dashboard',
  label: 'Home',
  icon: LayoutGrid,
  to: '/dashboard',
};

const OPPORTUNITIES: NavSection = {
  id: 'opportunities',
  label: 'Opportunities',
  icon: Search,
  to: '/opportunities',
};

const CREATOR_CAMPAIGNS: NavSection = {
  id: 'campaigns',
  label: 'Campaigns',
  icon: Megaphone,
  to: '/campaigns',
};

const PROPERTIES: NavSection = {
  id: 'properties',
  label: 'Properties',
  icon: Clapperboard,
  to: '/properties',
};

const LINK_IN_BIO: NavSection = {
  id: 'linkInBio',
  label: 'Link in bio',
  icon: LinkIcon,
  to: '/link-in-bio',
};

export interface NavConfig {
  /** Main destinations (nested for brands, flat for creators). */
  primary: NavSection[];
  /** Secondary utilities under a divider. */
  secondary: NavSection[];
  /** The four the mobile bar shows before "More". */
  mobileBar: NavSection[];
}

/** Brand orgs: the org IS its property. Creator orgs: same shell, flat.
 * A brand invited into another brand's campaigns is the creator side there,
 * so it also gets the Offers inbox — only then, so the nav stays lean.
 * `hasInbox` adds org messaging (any org with the feature on). */
export const buildNav = (
  personaType: string | undefined,
  hasCampaignInvitations: boolean,
  hasInbox = false,
): NavConfig => {
  if (personaType === 'creator') {
    return {
      primary: [
        CREATOR_HOME,
        OFFERS,
        OPPORTUNITIES,
        CREATOR_CAMPAIGNS,
        ...(hasInbox ? [INBOX] : []),
        PROPERTIES,
        LINK_IN_BIO,
      ],
      secondary: [NOTIFICATIONS],
      mobileBar: [CREATOR_HOME, OFFERS, OPPORTUNITIES, CREATOR_CAMPAIGNS],
    };
  }
  return {
    primary: [
      HOME,
      MARKET,
      CREATORS,
      CAMPAIGNS,
      ...(hasCampaignInvitations ? [OFFERS] : []),
      ...(hasInbox ? [INBOX] : []),
    ],
    secondary: [AI_CHAT, PROFILE, NOTIFICATIONS],
    mobileBar: [HOME, MARKET, CREATORS, CAMPAIGNS],
  };
};

/** Every item the mobile bar doesn't show goes into the More sheet. */
export const moreItems = (nav: NavConfig): NavSection[] => {
  const shown = new Set(nav.mobileBar.map((i) => i.id));
  return [...nav.primary, ...nav.secondary].filter((i) => !shown.has(i.id));
};

/** A section is active when it or any child is. */
export const isSectionActive = (
  section: NavSection,
  location: NavLocation,
): boolean =>
  isNavItemActive(section, location) ||
  (section.children ?? []).some((c) => isNavItemActive(c, location));

/** Shared look of a top-level row (expanded) or icon (collapsed). */
export const navItemClass = (active: boolean, collapsed: boolean): string =>
  cn(
    'relative flex min-w-0 items-center rounded-lg text-sm font-medium outline-none transition-colors [-webkit-tap-highlight-color:transparent] focus-visible:ring-2 focus-visible:ring-marble-blue-500',
    collapsed ? 'size-11 justify-center' : 'h-9 flex-1 gap-3 px-2.5',
    active
      ? 'bg-card text-marble-blue-700 dark:bg-marble-bg-100'
      : 'text-marble-bg-600 hover:bg-card hover:text-marble-blue-700 dark:hover:bg-marble-bg-100',
  );
